import { STORY_SCENE_IDS } from "../../src/game/story";
import { NO_CONSUMABLES } from "../../src/game/Consumable";
import { GameState } from "../../src/game/GameState";
import { ALL_ARTICLE_IDS } from "../../src/game/manual";
import { STARTER_SKILLS } from "../../src/game/Skill";
import { MachineState } from "../../src/game/Machine";
import { Board, Panel } from "../../src/game/Materials";

function idleMachine(
  machineTypeId: MachineState["machineTypeId"],
  position: [number, number],
  selectedOperationId: string,
  tools: MachineState["tools"] = [],
): MachineState {
  return {
    machineTypeId,
    position,
    rotation: 0,
    inputMaterials: [],
    processingMaterials: [],
    outputMaterials: [],
    tools,
    selectedOperationId,
    selectedParameters: undefined,
    operationProgress: {
      status: "notStarted",
      phaseIndex: 0,
      minutesRemaining: 0,
    },
    // This spec is about the end-grain chain, not the power switch — the
    // saw starts switched on (milling.spec covers the switch itself).
    poweredOn: true,
  };
}

/** A sanded single-species maple panel, ready for the crosscut sled. */
const sandedBlank: Panel = {
  id: "test-eg-blank",
  type: "panel",
  length: 24,
  thickness: 4,
  surface: "sanded",
  strips: Array.from({ length: 5 }, () => ({
    species: "maple" as const,
    width: 2 as const,
  })),
};

function palletBoard(id: string): Board {
  return {
    id,
    type: "board",
    species: "pallet",
    length: 36,
    width: 4,
    thickness: 4,
    surface: "rough",
    jointedFaces: 1,
    jointedEdges: 2,
  };
}

/**
 * Everything for the end-grain chain except the plywood (bought in the
 * store, to cover the Sheet Goods aisle): workspace with sander at [1,2],
 * sales table at [3,2], table saw at [2,4] with a free tool slot, two
 * pallet scraps for sled runners, a sanded maple panel to slice, and 2
 * skill points for Jigs & Fixtures + End-Grain Boards.
 */
export const endGrainShop: GameState = {
  minute: 0,
  day: 1,
  dayStartMinute: 0,
  money: 100,
  reputation: 20,
  // The sled build reads its screw bill off its blueprint now
  consumables: { ...NO_CONSUMABLES, screws: 8 },
  // Enough clamps for the end-grain glue-up (4) plus a pair to spare.
  clamps: 6,
  materialPiles: [],
  player: {
    name: "Player",
    position: [1, 4], // the workspace's operation cell
    direction: 0,
    // The sled's runners wait in the bench's bay — the arms only hold so
    // much, and the spec's hands are for the panel and the plywood.
    inventory: [sandedBlank],
    away: null,
  },
  machines: [
    {
      ...idleMachine("workspace", [1, 2], "dismantlePallet", [
        "randomOrbitSander",
        "finishingKit",
      ]),
      inputMaterials: [
        palletBoard("test-runner-1"),
        palletBoard("test-runner-2"),
      ],
    },
    idleMachine("jobsiteTableSaw", [6, 3], "ripBoard"),
  ],
  machineCrates: [],
  truck: { bed: [], crates: [] },
  storage: {
    upgrades: [],
  },
  shopInfo: {
    name: "One Car Garage",
    electricity: 120,
    size: [12, 16],
    materialDropoffPosition: [10, 13],
    entrancePosition: [6, 15],
  },
  progression: {
    tutorials: {
      opening: { step: 0, dismissed: true },
      firstNight: { step: 1, dismissed: false },
      dust: { step: 0, dismissed: false },
    },
    storeUnlocked: true,
    standUnlocked: true,
    // A staged shop is past the opening: every scene has played
    story: { scenesPlayed: [...STORY_SCENE_IDS] },
    lumberyardUnlocked: true,
    salesCompleted: 5,
    // Lets specs use the speed keys to fast-forward through glue cures
    sweepingUnlocked: false,
    unlockedArticles: ALL_ARTICLE_IDS,
    readArticles: ALL_ARTICLE_IDS,
    xp: 0,
    skillPoints: 2,
    unlockedSkills: STARTER_SKILLS,
  },
  stand: [],
  standSetUp: true,
  customers: [],
  dust: {},
  shopVac: null,
  broomOwned: true,
  broomPosition: [0, 0],
  dustpan: {},
};
